import React from 'react'
import { Input, notification } from 'antd'
import { useHistory } from 'react-router'
import { useAuth } from '@nic/kc-auth'
import type { NicScope } from '@nic/nic-api/types'
import { getProxiedRequest, getRequest } from '@nic/nic-api'
import { useTranslation } from '@nic/commons'
import { findRequestByDomain } from '../../Services/requestSearch'
import { getSanitizedQuickSearchValue, resolveRequestQuickSearchKind } from './requestQuickSearchUtils'

type RequestQuickSearchVariant = 'desktop' | 'mobile'

interface RequestQuickSearchProps {
  variant?: RequestQuickSearchVariant
}

const { Search } = Input

export const RequestQuickSearch: React.FC<RequestQuickSearchProps> = ({ variant = 'desktop' }) => {
  const { keycloak } = useAuth()
  const history = useHistory()
  const t = useTranslation()
  const realm = (keycloak?.realm as NicScope) || 'registrar'
  const isRegistrar = realm === 'registrar'

  const [value, setValue] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const skipNextSearchRef = React.useRef(false)
  const showError = (translationKey: string, params?: Record<string, string | number>) => {
    const description = t(translationKey, params)
    notification.error({
      message: t('requestQuickSearch.notificationTitle'),
      description,
      placement: 'top',
    })
  }

  const redirectToRequest = (requestId: number) => {
    history.push(`/${realm}/requests/${requestId}`)
  }

  const verifyRequestById = async (requestId: number) => {
    if (isRegistrar) {
      await getProxiedRequest(requestId)
    } else {
      await getRequest(requestId)
    }
  }

  const handleSearch = async (
    submittedValue?: string,
    event?: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLElement>,
  ) => {
    console.log(event)
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false
      return
    }

    const target = event?.target as HTMLElement | null
    if (event?.type === 'click' && target?.closest('.ant-input-clear-icon')) {
      return
    }

    const searchValue = submittedValue ?? value
    const sanitizedValue = getSanitizedQuickSearchValue(searchValue)

    if (!sanitizedValue) {
      showError('requestQuickSearch.errors.empty')
      return
    }

    const searchKind = resolveRequestQuickSearchKind(sanitizedValue)

    if (!searchKind) {
      showError('requestQuickSearch.errors.invalid')
      return
    }

    setLoading(true)

    try {
      if (searchKind === 'id') {
        const requestId = Number(sanitizedValue)
        await verifyRequestById(requestId)
        setValue('')
	        redirectToRequest(requestId)
	        return
	      }

      const result = await findRequestByDomain(sanitizedValue, realm)

      if (!result?.requestId) {
        showError('requestQuickSearch.errors.notFoundByDomain', { value: sanitizedValue })
        return
      }

      setValue('')
	      redirectToRequest(result.requestId)
    } catch (error: any) {
      const statusCode = error?.response?.status
      if (searchKind === 'id' && statusCode === 404) {
        showError('requestQuickSearch.errors.notFoundById', { value: sanitizedValue })
      } else if (searchKind === 'domain' && statusCode === 404) {
        showError('requestQuickSearch.errors.notFoundByDomain', { value: sanitizedValue })
      } else {
        showError('requestQuickSearch.errors.generic')
      }
    } finally {
      setLoading(false)
    }
  }

  const isDesktop = variant === 'desktop'

  const handleMouseDownCapture = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null
    if (target?.closest('.ant-input-clear-icon')) {
      skipNextSearchRef.current = true
    }
  }, [])

  return (
    <div style={{ width: isDesktop ? 'auto' : '100%' }} onMouseDownCapture={handleMouseDownCapture}>
      <Search
        value={value}
        onChange={(event) => {
          setValue(event.target.value)
        }}
        onSearch={handleSearch}
        placeholder={t('requestQuickSearch.placeholder')}
        enterButton={t('requestQuickSearch.button')}
        loading={loading}
        allowClear
        size={isDesktop ? 'middle' : 'large'}
        style={{
          minWidth: isDesktop ? 220 : undefined,
          maxWidth: isDesktop ? 360 : '100%',
          width: isDesktop ? '100%' : undefined,
        }}
        data-testid={`request-quick-search-${variant}`}
      />
    </div>
  )
}
